/**
 * Authentication Redirect Service
 *
 * Single Responsibility: Handle all authentication-related redirects
 * Separation of Concerns: Isolate redirect logic from auth state and error handling
 * DRY: Centralize redirect logic to prevent duplication
 */

/**
 * Centralized authentication redirect service
 * Handles all scenarios where users need to be redirected after auth state changes
 */
export class AuthRedirectService {
  /**
   * Redirect user to root page after authentication failure
   * Works in both client-side routing and full page navigation contexts
   */
  static redirectToRoot(): void {
    if (typeof window === "undefined") return;

    // Check if we're in a Next.js routing context
    const isNextJsContext = window.location.pathname.startsWith("/");

    if (isNextJsContext) {
      // Use full page navigation for consistency across different scenarios
      window.location.href = "/";
    }
  }

  /**
   * Redirect user with Next.js router (for client-side navigation)
   * @param router - Next.js router instance
   * @param redirectTo - Optional custom redirect path (defaults to root)
   */
  static redirectWithRouter(router: any, redirectTo?: string): void {
    const targetPath = redirectTo || "/";
    router.push(targetPath);
  }

  /**
   * Get the appropriate redirect path for unauthenticated users
   * @param customPath - Optional custom redirect path
   * @returns The redirect path to use
   */
  static getUnauthenticatedRedirectPath(customPath?: string): string {
    return customPath || "/";
  }
}
